import clsx from 'clsx';
import type {
  MetricHistoryEntry,
  OutputThresholds,
  SignalDotState,
} from '@arcanum-hq/shared-types';
import type { ChipVariant } from '@/components/ui/Chip';
import type { ThresholdFormat } from '../ThresholdBand';
import { formatCurrency, formatMultiple, formatPercent } from '@/lib/format';

// SignalDotState lifted to @arcanum-hq/shared-types by deal-signal-overhaul
// Phase 4 Session 1 (2026-05-11) — same 4-value vocabulary; semantic alias
// of `signalSchema` named to match the component prop convention. Re-exported
// here for back-compat with existing call sites that import from this module.
export type { SignalDotState };

// Per-state hex for the inline dot glow (the inline boxShadow needs a real
// color — Tailwind's `bg-*` classes don't propagate to box-shadow).
const SIGNAL_DOT_HEX: Record<SignalDotState, string> = {
  aligned: '#00c853', // @design-token resolves --color-accent
  aggressive: '#d4960f', // @design-token resolves --color-warn
  conservative: '#3a78b8', // @design-token resolves --color-blue
  differentiated: '#7860a8', // @design-token resolves --color-purple
};

const SIGNAL_DOT_BG: Record<SignalDotState, string> = {
  aligned: 'bg-accent',
  aggressive: 'bg-warn',
  conservative: 'bg-blue',
  differentiated: 'bg-purple',
};

export interface HeroMetricCellProps {
  /** Schema name (e.g. `IRR`) — stable React key + walkthrough probe. */
  namedRange: string;
  label: string;
  /** `null` when the engine cannot compute the value (missing inputs). */
  value: number | null;
  format: ThresholdFormat;
  thresholds?: OutputThresholds;
  history?: MetricHistoryEntry[];
  /**
   * Legacy prop preserved for type compatibility; the Lovable-aligned hero
   * cell no longer surfaces a source chip in the header row (density).
   */
  chipVariant?: ChipVariant;
  signalDot?: SignalDotState | null;
  positiveTrend?: 'up' | 'down';
  /** When true, omit the right-edge divider (last cell in the strip). */
  isLast?: boolean;
  className?: string;
}

const MULTIPLE_OVERRIDE_NAMES: ReadonlySet<string> = new Set([
  'EquityMultiple',
  'GPEquityMultiple',
  'LPEquityMultiple',
]);

function formatValue(v: number, format: ThresholdFormat): string {
  if (format === 'percentage') return formatPercent(v, 1);
  if (format === 'currency') return formatCurrency(v, 0);
  return formatMultiple(v);
}

/**
 * Hero-row metric cell — mirrors the Lovable reference's compact terminal
 * cell (`dashboard-arcanum/src/pages/PublicDealView.tsx:334-370`). No outer
 * card border / radius — adjacent cells separate via a right-edge hairline.
 * Tight padding (10px / 14px), 7px uppercase label, 18px accent-green value,
 * 5px signal dot with glow at the value baseline, 1px bottom divider.
 *
 * ThresholdBand + Sparkline removed from this surface — Lovable's Signal-tab
 * hero strip stays dense and lets the band logic live elsewhere (the legacy
 * `<HeroMetricsHeader>` on the Model tab consumes `<PerformanceTile>` which
 * keeps the band + sparkline pattern).
 */
export function HeroMetricCell({
  namedRange,
  label,
  value,
  format,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  thresholds: _thresholds,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  history: _history,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  chipVariant: _chipVariant,
  signalDot = null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  positiveTrend: _positiveTrend,
  isLast = false,
  className,
}: HeroMetricCellProps) {
  const hasValue = value != null && Number.isFinite(value);
  const effectiveFormat: ThresholdFormat = MULTIPLE_OVERRIDE_NAMES.has(
    namedRange
  )
    ? 'multiplier'
    : format;

  return (
    <div
      data-testid="hero-metric-cell"
      data-named-range={namedRange}
      data-has-value={hasValue ? 'true' : 'false'}
      className={clsx(
        'flex-1 flex flex-col justify-end gap-[5px] px-[14px] py-[10px] min-w-0',
        !isLast && 'border-r border-panel-border',
        className
      )}
    >
      <div className="text-[7px] font-mono font-bold uppercase tracking-[0.6px] text-text-bright/90 leading-tight whitespace-pre-line">
        {label}
      </div>
      <div className="flex items-baseline gap-[7px]">
        <span
          data-testid="hero-metric-value"
          className="text-[18px] font-mono font-semibold tracking-[-0.4px] leading-none text-accent tabular-nums"
        >
          {hasValue ? formatValue(value as number, effectiveFormat) : '—'}
        </span>
        {signalDot && (
          <span
            data-testid="signal-dot"
            data-signal={signalDot}
            aria-label={`Signal: ${signalDot}`}
            className={clsx(
              'w-[5px] h-[5px] rounded-full shrink-0 self-center',
              SIGNAL_DOT_BG[signalDot]
            )}
            style={{ boxShadow: `0 0 4px ${SIGNAL_DOT_HEX[signalDot]}66` }}
          />
        )}
      </div>
      <div className="h-px w-full bg-panel-border mt-0.5" />
    </div>
  );
}
